// v0.4.92 — Drift health chip relabel + inline run + tightened
// tooltip (sign-off 2026-06-20 — 1a + follow-up tightening).
//
// Pre-v0.4.92 the chip read "Drift broken · never run" which the
// operator parsed as "this calc has drifted from reconciliation".
// In reality the chip is about TEDB rate-table verification —
// admin housekeeping that does NOT block filing. The collision
// between "drift" here and "drift" in reconciliation language was
// the actual confusion source.
//
// v0.4.92 follow-up — the explanatory copy used to live in a long
// `title=` tooltip. Tightened to a single sentence + an InfoCircle
// disclosure for the auditor-grade detail (matches the v0.4.89
// pattern used on box cards and the ActionRequiredBanner).
//
// 60s polling preserved. ADMIN-only. Hidden when state === "OK".
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchDriftHealth, verifyRatesAgainstTedb } from "../lib/api";
import { InfoCircle } from "./InfoCircle";

const POLL_MS = 60_000;

export const useDriftHealth = (enabled) => {
  const [health, setHealth] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const poll = async () => {
      const r = await fetchDriftHealth();
      if (!cancelled && r.ok) setHealth(r.body);
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled, tick]);
  return { health, refresh: () => setTick((t) => t + 1) };
};

export const DriftHealthChip = ({ isAdmin }) => {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { health, refresh } = useDriftHealth(isAdmin);
  const [running, setRunning] = useState(false);
  if (!isAdmin || !health) return null;
  if (health.state === "OK") return null;

  const tone = health.state === "BROKEN"
    ? "border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/40"
    : "border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/40";
  const dot = health.state === "BROKEN" ? "bg-rose-500" : "bg-amber-500";
  const label = formatLabel(health, t);
  const word = health.state === "BROKEN"
    ? t("ui.drift_chip.status_unverified")
    : t("ui.drift_chip.status_stale");

  const runCheck = async (e) => {
    e.stopPropagation();
    if (running) return;
    setRunning(true);
    await verifyRatesAgainstTedb();
    setRunning(false);
    refresh();
  };

  return (
    <span
      data-testid="header-drift-health-chip"
      data-state={health.state}
      className={`inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-full border ${tone}`}
      title={t("ui.drift_chip.tooltip_chip")}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
      <button
        type="button"
        data-testid="header-drift-health-open"
        onClick={() => nav("/settings")}
        className="hover:underline decoration-dotted"
      >
        {t("ui.drift_chip.label_prefix", { defaultValue: "Rate sources" })} {word} · {label}
      </button>
      <button
        type="button"
        data-testid="header-drift-health-run"
        onClick={runCheck}
        disabled={running}
        className={`text-[10px] tabular-nums uppercase tracking-wide px-1.5 py-0.5 rounded-full border ${
          running
            ? "border-slate-300 dark:border-slate-700 text-slate-400 dark:text-slate-500 cursor-wait"
            : health.state === "BROKEN"
              ? "border-rose-300 dark:border-rose-700 hover:bg-rose-100 dark:hover:bg-rose-900/60"
              : "border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/60"
        }`}
        title={t("ui.drift_chip.tooltip_run")}
      >
        {running ? t("ui.drift_chip.running") : t("ui.drift_chip.run_check")}
      </button>
      <InfoCircle testid="header-drift-health-info" align="right">
        <div className="font-semibold text-slate-900 dark:text-slate-50 mb-1">
          {t("ui.drift_chip.info_title")}
        </div>
        <div
          className="text-slate-700 dark:text-slate-200 mb-2"
          dangerouslySetInnerHTML={{ __html: t("ui.drift_chip.info_body_html") }}
        />
        <div className="tabular-nums text-[11px] text-slate-600 dark:text-slate-300 leading-snug border-t border-slate-200 dark:border-slate-700 pt-2">
          {t("ui.drift_chip.info_last_check")} <span className="font-semibold">
            {health.last_successful_run_at || t("ui.drift_chip.info_never")}
          </span>
          <br />
          {t("ui.drift_chip.info_thresholds", { stale: health.thresholds.stale_hours, broken: health.thresholds.broken_hours })}
          <br />
          {t("ui.drift_chip.info_latest_outcome")}{" "}
          <span className="font-semibold">
            {health.latest_outcome || "—"}
          </span>
        </div>
      </InfoCircle>
    </span>
  );
};

function formatLabel(h, t) {
  if (h.age_hours == null) return t("ui.drift_chip.age_never");
  const hrs = h.age_hours;
  if (hrs < 24) return `${Math.round(hrs)}h`;
  return `${Math.round(hrs / 24)}d`;
}

export default DriftHealthChip;
